import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IAppState } from '../app/app-state';
import { Action, Store } from '@ngrx/store';
import { PayrollDataService } from '@core/data/api/payroll/payroll-data.service';
import {
    EMPLOYEES_SUGGESTIONS_FETCH,
    ROUTER_NAVIGATION,
    SETTINGS_FETCH,
    SETTINGS_LEAVEPOLICIES_FETCH,
    SETTINGS_LEAVEPOLICY_FETCH,
    SETTINGS_LEAVEPOLICY_EMPLOYEES_UPDATE,
    SETTINGS_UPDATE,
    SETTINGS_TIMEOFF_TYPES_FETCH,
    SETTINGS_LEAVEPOLICY_CREATE
} from '../action-types';
import {
    FetchLeavePolicyAction,
    FetchSettingsAction,
    UpdateLeavePolicyEmployeesAction,
    SetEmployeeSuggestionsAction,
    SetEmployeeSuggestionsFetchingAction,
    SetFetchingSettingsAction,
    SetLeavePoliciesAction,
    SetLeavePoliciesFetchingAction,
    SetLeavePolicyAction,
    SetSettingsAction,
    SetViewedLeavePolicyAction,
    UpdateSettingsAction,
    FetchTimeoffTypesAction,
    SetTimeoffTypesAction,
    CreateLeavePolicyAction,
    ResetNewLeavePolicyAction
} from './settings-actions';
import { mergeMap, switchMap } from 'rxjs/operators';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { from } from 'rxjs';
import { IAppRouterState } from '../router/router-state';
import {
    SETTINGS_MAPPING_PAYROLL,
    SETTINGS_GENERAL,
    SETTINGS_WORKPLACES,
    SETTINGS_LEAVES_POLICIES,
    SETTINGS_LEAVES_POLICIES_DETAILS,
    SETTINGS_LEAVES_POLICIES_DETAILS_EMPLOYEES,
    SETTINGS_LEAVES_POLICIES_CREATE_STEP2,
    SETTINGS_LEAVES_POLICIES_CREATE_STEP4,
    SETTINGS_LEAVES_POLICIES_CREATE_STEP3,
    SETTINGS_LEAVES_POLICIES_CREATE_STEP5,
    SETTINGS_LEAVES_POLICIES_CREATE,
    SETTINGS_LEAVES_POLICIES_CREATE_STEP1,
    NO_STATE
} from '@core/router/app-states';
import { FetchWorkplacesAction } from '../workplaces/workplaces-actions';
import { FetchEmployeeSuggestionsAction, FetchLeavePoliciesAction } from './settings-actions';
import { Employee } from '@core/models/employee/employee';
import { EMPLOYEE_ENTITY, LEAVE_POLICY_ENTITY } from '../entity-types';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';
import { LeavePolicy } from '@core/models/leave/leave-policy';
import { schema } from 'normalizr';
import { RouterGoAction, RouterNavigationCancelAction } from '../router/router-actions';
import { TimeoffDataService } from '@core/data/api/time-off/time-off-data.service';
import { TimeOffType } from '@core/models/time-off/time-off-type';
import { CreateLeavePolicyRequest } from '@core/models/leave/create-leave-policy-request';

@Injectable()
export class SettingsEffects {

    constructor(private payrollDataService: PayrollDataService,
                private timeoffDataService: TimeoffDataService,
                private store$: Store<IAppState>,
                private actions$: Actions) {
    }

    fetchSettings$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchSettingsAction>(SETTINGS_FETCH),
            switchMap(() => {
                return this.payrollDataService.fetchSettings()
                .pipe(
                    mergeMap((response: any) => {
                        const settings: any = response;
                        return [
                            new SetSettingsAction({ settings }),
                            new SetFetchingSettingsAction(false)
                        ];
                    }),
                    
                );
            }),
            ossCatchError(([errorActions]) => from(errorActions
                .concat([new SetFetchingSettingsAction(false)]))
            )
        )
    );

    updateSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateSettingsAction>(SETTINGS_UPDATE),
            switchMap((action: UpdateSettingsAction) => {
                return this.payrollDataService.updateSettings(action.payload.settings)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response),
                                new SetSettingsAction({ settings: response })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    fetchEmployeeSuggestions$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchEmployeeSuggestionsAction>(EMPLOYEES_SUGGESTIONS_FETCH),
            switchMap(
                (action: FetchEmployeeSuggestionsAction) => {

                    return this.payrollDataService.fetchEmployees()
                        .pipe(
                            ossNormalize(EMPLOYEE_ENTITY, Employee, 'employees'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetEmployeeSuggestionsAction(normalized),
                                    new SetEmployeeSuggestionsFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions, error]) => from(actions.concat([new SetEmployeeSuggestionsFetchingAction(false)])))
                        );
                }
            )
        )
    );

    fetchLeavePolicies$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchLeavePoliciesAction>(SETTINGS_LEAVEPOLICIES_FETCH),
            switchMap(
                (action: FetchLeavePoliciesAction) => {

                    return this.payrollDataService.fetchLeavePolicies()
                        .pipe(
                            ossNormalize(LEAVE_POLICY_ENTITY, LeavePolicy, 'policies'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetLeavePoliciesAction(normalized),
                                    new SetLeavePoliciesFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions, error]) => from(actions.concat([new SetLeavePoliciesFetchingAction(false)])))
                        );
                }
            )
        )
    );

    fetchLeavePolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchLeavePolicyAction>(SETTINGS_LEAVEPOLICY_FETCH),
            switchMap((action: FetchLeavePolicyAction) => {

                const id: string = action.payload.id;

                return this.payrollDataService.fetchLeavePolicy(action.payload.id)
                    .pipe(
                        mergeMap((response: any) => {

                            return [
                                new SetLeavePolicyAction({
                                    item: LeavePolicy.import(response),
                                    schema: new schema.Entity(LEAVE_POLICY_ENTITY)
                                })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    );
            })
        )
    );

    createLeavePolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType<CreateLeavePolicyAction>(SETTINGS_LEAVEPOLICY_CREATE),
            switchMap((action: CreateLeavePolicyAction) => {

                const request: CreateLeavePolicyRequest = action.payload.request;

                return this.payrollDataService.createLeavePolicy(request)
                    .pipe(
                        switchMap((response: any) => {
                            return [
                                (action as any).success(response),
                                new ResetNewLeavePolicyAction()
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    );
            })
        )
    );

    updateLeavePolicyEmployees$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateLeavePolicyEmployeesAction>(SETTINGS_LEAVEPOLICY_EMPLOYEES_UPDATE),
            switchMap((action: UpdateLeavePolicyEmployeesAction) => {

                const { id, request }: any = action.payload;

                return this.payrollDataService.updateLeavePolicyEmployees(id, { members: request })
                    .pipe(
                        mergeMap((response: any) => {

                            return [
                                (action as any).success(response ),
                                new FetchLeavePolicyAction({ id })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from([
                            (action as any).error(error)
                        ]))
                    );
            })
        )
    );

    fetchTimeoffTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchTimeoffTypesAction>(SETTINGS_TIMEOFF_TYPES_FETCH),
            switchMap((action: FetchTimeoffTypesAction) => {
                return this.timeoffDataService.fetchTimeoffTypes()
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                new SetTimeoffTypesAction({ 
                                    types: (response.timeoff_types || [])
                                                .map((type) => Object.assign(new TimeOffType(), type))

                                })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions))
                    );
            })
        )
    );

    routerSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            mergeMap((action) => {
                const routerState: IAppRouterState = (action as any).payload
                .routerState as IAppRouterState;
                const { name: stateName, fromState, params } = routerState;
                const actions: Action[] = [];

                if (stateName === SETTINGS_GENERAL) {
                    actions.push(new FetchSettingsAction());

                } else if (stateName === SETTINGS_WORKPLACES) {
                    actions.push(new FetchWorkplacesAction());

                } else if (stateName === SETTINGS_MAPPING_PAYROLL) {
                    actions.push(new FetchEmployeeSuggestionsAction());

                } else if (stateName === SETTINGS_LEAVES_POLICIES) {
                    actions.push(new FetchLeavePoliciesAction());
                    actions.push(new FetchTimeoffTypesAction());

                } else if (stateName === SETTINGS_LEAVES_POLICIES_DETAILS) {
                    actions.push(new FetchLeavePolicyAction({ id: params.id }));
                    actions.push(new SetViewedLeavePolicyAction({ id: params.id }));

                } else if (stateName === SETTINGS_LEAVES_POLICIES_DETAILS_EMPLOYEES) {

                    if (fromState.name !== SETTINGS_LEAVES_POLICIES_DETAILS) {
                        actions.push(new RouterNavigationCancelAction({ state: SETTINGS_LEAVES_POLICIES_DETAILS_EMPLOYEES }));
                        actions.push(new RouterGoAction({ name: SETTINGS_LEAVES_POLICIES }));
                    }
                } else if (stateName === SETTINGS_LEAVES_POLICIES_CREATE_STEP1) {

                    if (fromState.name === NO_STATE) {
                        actions.push(new FetchTimeoffTypesAction());
                    }

                    if (fromState.name.indexOf(SETTINGS_LEAVES_POLICIES_CREATE) === -1) {
                        actions.push(new ResetNewLeavePolicyAction());
                    }

                } else if (stateName === SETTINGS_LEAVES_POLICIES_CREATE_STEP2 ||
                    stateName === SETTINGS_LEAVES_POLICIES_CREATE_STEP3 ||
                    stateName === SETTINGS_LEAVES_POLICIES_CREATE_STEP4 ||
                    stateName === SETTINGS_LEAVES_POLICIES_CREATE_STEP5
                ) {

                    if (fromState.name.indexOf(SETTINGS_LEAVES_POLICIES_CREATE) === -1) {
                        actions.push(new RouterNavigationCancelAction({ state: stateName }));
                        actions.push(new RouterGoAction({ name: SETTINGS_LEAVES_POLICIES }));
                    }
                }

                return actions;
            })
        )
    );
}
